import { useState, useMemo } from "react";
import { Package, RefreshCw, ChevronRight } from "lucide-react";

export const OpenPositions = ({ openPositionsData, onRefresh }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  const positions = openPositionsData?.open_positions || [];
  const groupedByAsset = openPositionsData?.grouped_by_asset || [];
  const totalUnrealized = openPositionsData?.total_unrealized_pnl || 0;
  const spotSummary = openPositionsData?.spot_summary;

  const visibleAssets = useMemo(
    () => groupedByAsset.filter(a => a.total_unrealized_pnl !== null && a.total_unrealized_pnl !== 0),
    [groupedByAsset]
  );

  if (!openPositionsData || openPositionsData.trade_type !== 'spot') return null;

  if (positions.length === 0 && groupedByAsset.length === 0) {
    return (
      <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Package className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Posiciones Abiertas (Spot)</h3>
        </div>
        <p className="text-gray-500">No hay posiciones abiertas en este momento.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6 mb-8" data-testid="open-positions">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Posiciones Abiertas (Spot)</h3>
          <span className="text-gray-500 text-sm">({groupedByAsset.length} activos)</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={onRefresh} className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors" data-testid="refresh-positions">
            <RefreshCw className="w-4 h-4" /> Actualizar precios
          </button>
          <div className={`px-4 py-2 rounded-lg ${totalUnrealized >= 0 ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-red-500/20 border border-red-500/40'}`}>
            <span className="text-gray-400 text-sm mr-2">PNL No Realizado:</span>
            <span className={`font-bold text-lg ${totalUnrealized >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {totalUnrealized >= 0 ? '+' : ''}{totalUnrealized.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT
            </span>
          </div>
        </div>
      </div>

      {spotSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs mb-1">PNL Realizado</p>
            <p className={`text-lg font-bold ${spotSummary.total_realized_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {spotSummary.total_realized_pnl >= 0 ? '+' : ''}{spotSummary.total_realized_pnl?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT
            </p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs mb-1">Total Comisiones</p>
            <p className="text-orange-400 font-bold text-lg">-{spotSummary.total_fees?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs mb-1">Trades Cerrados</p>
            <p className="text-white font-bold text-lg">{spotSummary.closed_trades}</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs mb-1">Pares Operados</p>
            <p className="text-white font-bold text-lg">{spotSummary.pairs_traded}</p>
          </div>
        </div>
      )}

      <div className="mb-4">
        <h4 className="text-white font-medium mb-3">Resumen por Activo</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 text-gray-400 font-medium">Activo</th>
                <th className="text-center py-3 text-gray-400 font-medium">Fecha Inicio</th>
                <th className="text-center py-3 text-gray-400 font-medium">Días</th>
                <th className="text-right py-3 text-gray-400 font-medium">Cantidad</th>
                <th className="text-right py-3 text-gray-400 font-medium">Precio Compra</th>
                <th className="text-right py-3 text-gray-400 font-medium">Costo Total</th>
                <th className="text-right py-3 text-gray-400 font-medium">Precio Actual</th>
                <th className="text-right py-3 text-gray-400 font-medium">Valor Actual</th>
                <th className="text-right py-3 text-gray-400 font-medium">PNL</th>
                <th className="text-right py-3 text-gray-400 font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {visibleAssets.map((asset) => (
                <tr key={asset.asset} className="border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold text-base">{asset.asset}</span>
                      <span className="text-gray-500 text-xs">({asset.positions_count} pos.)</span>
                    </div>
                  </td>
                  <td className="py-3 text-center text-gray-300 text-xs">{asset.first_buy_date || '-'}</td>
                  <td className="py-3 text-center">
                    {asset.days_held !== null ? (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        asset.days_held > 365 ? 'bg-purple-500/20 text-purple-400' :
                        asset.days_held > 90 ? 'bg-blue-500/20 text-blue-400' :
                        asset.days_held > 30 ? 'bg-cyan-500/20 text-cyan-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>{asset.days_held}d</span>
                    ) : '-'}
                  </td>
                  <td className="py-3 text-right text-gray-300">{asset.total_quantity?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 6})}</td>
                  <td className="py-3 text-right text-gray-300">${asset.avg_buy_price?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 4})}</td>
                  <td className="py-3 text-right text-gray-300">${asset.total_cost_basis?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                  <td className="py-3 text-right text-white font-medium">{asset.current_price ? `$${asset.current_price.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 4})}` : '-'}</td>
                  <td className="py-3 text-right text-white">{asset.total_current_value ? `$${asset.total_current_value.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-'}</td>
                  <td className={`py-3 text-right font-bold text-base ${asset.total_unrealized_pnl === null ? 'text-gray-500' : asset.total_unrealized_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {asset.total_unrealized_pnl !== null ? `${asset.total_unrealized_pnl >= 0 ? '+' : ''}${asset.total_unrealized_pnl.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-'}
                  </td>
                  <td className={`py-3 text-right font-semibold ${asset.pnl_percentage === null ? 'text-gray-500' : asset.pnl_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {asset.pnl_percentage !== null ? `${asset.pnl_percentage >= 0 ? '+' : ''}${asset.pnl_percentage.toFixed(2)}%` : '-'}
                  </td>
                </tr>
              ))}
              {(() => {
                const filteredAssets = groupedByAsset.filter(a => a.total_unrealized_pnl !== null && a.total_unrealized_pnl !== 0);
                const totalCost = filteredAssets.reduce((sum, a) => sum + (a.total_cost_basis || 0), 0);
                const totalValue = filteredAssets.reduce((sum, a) => sum + (a.total_current_value || 0), 0);
                const totalPnl = filteredAssets.reduce((sum, a) => sum + (a.total_unrealized_pnl || 0), 0);
                const totalPct = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
                return (
                  <tr className="border-t-2 border-gray-600 bg-gray-900/50">
                    <td className="py-4"><span className="text-white font-bold text-base">TOTAL</span></td>
                    <td className="py-4 text-center text-gray-500">-</td>
                    <td className="py-4 text-center text-gray-500">-</td>
                    <td className="py-4 text-right text-gray-500">-</td>
                    <td className="py-4 text-right text-gray-500">-</td>
                    <td className="py-4 text-right text-white font-bold">${totalCost.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-4 text-right text-gray-500">-</td>
                    <td className="py-4 text-right text-white font-bold">${totalValue.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className={`py-4 text-right font-bold text-lg ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </td>
                    <td className={`py-4 text-right font-bold text-lg ${totalPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {totalPct >= 0 ? '+' : ''}{totalPct.toFixed(2)}%
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-gray-800 pt-4">
        <button onClick={() => setShowDetails(!showDetails)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
          <ChevronRight className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
          {showDetails ? 'Ocultar' : 'Ver'} detalle de {positions.length} posiciones individuales
        </button>
      </div>

      {showDetails && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 text-gray-400 font-medium">Par</th>
                <th className="text-right py-3 text-gray-400 font-medium">Cantidad</th>
                <th className="text-right py-3 text-gray-400 font-medium">Precio Compra</th>
                <th className="text-right py-3 text-gray-400 font-medium">Costo Base</th>
                <th className="text-right py-3 text-gray-400 font-medium">Precio Actual</th>
                <th className="text-right py-3 text-gray-400 font-medium">Valor Actual</th>
                <th className="text-right py-3 text-gray-400 font-medium">PNL</th>
                <th className="text-right py-3 text-gray-400 font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <tr key={`${pos.trading_pair}-${pos.buy_timestamp}`} className="border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                  <td className="py-2 text-white font-medium">{pos.trading_pair}</td>
                  <td className="py-2 text-right text-gray-300">{pos.quantity?.toFixed(6)}</td>
                  <td className="py-2 text-right text-gray-300">${pos.avg_buy_price?.toFixed(4)}</td>
                  <td className="py-2 text-right text-gray-300">${pos.cost_basis?.toFixed(2)}</td>
                  <td className="py-2 text-right text-white">{pos.current_price ? `$${pos.current_price.toFixed(4)}` : '-'}</td>
                  <td className="py-2 text-right text-white">{pos.current_value ? `$${pos.current_value.toFixed(2)}` : '-'}</td>
                  <td className={`py-2 text-right font-semibold ${pos.unrealized_pnl === null ? 'text-gray-500' : pos.unrealized_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pos.unrealized_pnl !== null ? `${pos.unrealized_pnl >= 0 ? '+' : ''}${pos.unrealized_pnl.toFixed(2)}` : '-'}
                  </td>
                  <td className={`py-2 text-right font-medium ${pos.pnl_percentage === null ? 'text-gray-500' : pos.pnl_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pos.pnl_percentage !== null ? `${pos.pnl_percentage >= 0 ? '+' : ''}${pos.pnl_percentage.toFixed(2)}%` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <p className="text-gray-500 text-xs mt-4">
        * Los precios se obtienen en tiempo real de MEXC. El PNL no realizado no incluye comisiones de venta.
      </p>
    </div>
  );
};
